import FilterIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js';
import UndoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Undo.js';
import { FilterToolbar } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import React, { ComponentProps, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { ActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { EmptyState, HeroMetricCard } from 'src/components/atlas/molecules';
import {
  RowModel,
  SpeakUpTable,
} from 'src/components/atlas/organisms/SpeakUpTable/SpeakUpTable';
import emptyStateImage from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { CaseCreateNewButton } from 'src/components/molecules/CaseCreateNewButton/CaseCreateNewButton';
import { AppliedFiltersType } from 'src/graphql';
import {
  SearchValueProps,
  useCalculateTableRows,
  useCaseSearchQueryParams,
  useIsFeatureEnabled,
  usePageQueryParams,
  useRedirect,
} from 'src/hooks';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import * as Types from '../../../types';
import { FeatureFlag } from '../../../types';
import {
  buildCustomFieldSearch,
  buildSearchValue,
} from './helpers/speakUpFilterHelpers';
import { useCaseMetrics } from './hooks/useCaseMetrics';
import * as styles from './SpeakUpCases.styles';
import { PageContentBox, toggleGroup } from './SpeakUpCases.styles';
import { SpeakUpCasesHeader } from './SpeakUpCasesHeader';
import {
  SpeakUpFilterPopover,
  SpeakUpFilterRow,
  SpeakUpFilterValue,
} from './SpeakUpFilterPopover';
import { useCaseStatusTab } from './useCaseStatusTab';
import { useCaseTable } from './useCaseTable';
import { useSpeakUpCustomFields } from './useSpeakUpCustomFields';

const PADDING = 24;
const ACTIONS_ROW = 40;
const MARGIN = 20;
const TABLE_HEADER = 56;
const ROW_HEIGHT = 56;
const MIN_ROWS = 7;
const PAGINATION_PADDING = 6;
const PAGE_SPACE_EXCLUDING_ROWS =
  2 * PADDING + ACTIONS_ROW + TABLE_HEADER + MARGIN + 2 * PAGINATION_PADDING;

export const SpeakUpCases: React.FC = () => {
  const { showRedirect, redirectTo, redirect } = useRedirect();
  const caseDataDownloadFeatureFlagEnabled = useIsFeatureEnabled(
    FeatureFlag.CASE_DATA_DOWNLOAD,
  );
  const caseLinkingEnabled = useIsFeatureEnabled(FeatureFlag.LINKING_CASES);
  const modernizationEnabled = useIsFeatureEnabled(
    FeatureFlag.SPEAK_UP_CASES_MODERNIZATION,
  );

  const { page, setPage, resetParams } = usePageQueryParams('/cases');
  const { rows } = useCalculateTableRows(
    ROW_HEIGHT,
    MIN_ROWS,
    PAGE_SPACE_EXCLUDING_ROWS,
  );

  const [numPages, setNumPages] = useState(page ?? 1);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersType>({});
  const [searchValue, setSearchValue] =
    useState<ComponentProps<typeof CreatableSelect>['value']>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const makeFilterId = () => `filter-${Math.random().toString(36).slice(2, 9)}`;
  const [filterRows, setFilterRows] = useState<SpeakUpFilterRow[]>([
    { id: makeFilterId(), column: '' },
  ]);
  const [filterValue, setFilterValue] = useState<SpeakUpFilterValue>({});
  const [appliedFilterValue, setAppliedFilterValue] =
    useState<SpeakUpFilterValue>({});

  const [appliedCustomFieldSearch, setAppliedCustomFieldSearch] = useState<
    { fieldId: string; value: string } | undefined
  >(undefined);

  const onFilterRowsChange = (rows: SpeakUpFilterRow[]) => setFilterRows(rows);
  const onFilterChange = (value: SpeakUpFilterValue) => setFilterValue(value);
  const onFilterApply = (
    value: SpeakUpFilterValue,
    rows: SpeakUpFilterRow[],
  ) => {
    setFilterValue(value);
    setAppliedFilterValue(value);
    setAppliedCustomFieldSearch(buildCustomFieldSearch(value));
    setPage(1);
    searchCases?.(buildSearchValue(rows, value));
  };
  const onRemoveAllFilters = () => {
    const initialRow = { id: makeFilterId(), column: '' as const };
    setFilterRows([initialRow]);
    setFilterValue({});
    setAppliedFilterValue({});
    setAppliedCustomFieldSearch(undefined);
    setPage(1);
    searchCases?.([]);
  };
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const {
    caseQueryParamFields,
    paramSearchValues,
    setCaseQueryParamFields,
    updateUrlParams,
  } = useCaseSearchQueryParams(queryParams);

  const {
    caseStatusTab,
    setCaseStatusTab,
    caseStatusFilter,
    handleCaseStatusTabChange,
  } = useCaseStatusTab({
    caseQueryParamFields,
    setCaseQueryParamFields,
    activeCard,
    setActiveCard,
    setPage,
  });

  const {
    columns,
    sortableColumns,
    tableData,
    isLoading,
    isError,
    caseSearchOptions,
    searchCases,
    addNewReport,
    addExistingReport,
    sortColumns,
    openReport,
    sortedColumn,
    count,
  } = useCaseTable(
    rows,
    page,
    redirect,
    {
      ...appliedFilters,
      ...(modernizationEnabled ? { caseStatus: caseStatusFilter } : {}),
    },
    caseQueryParamFields,
    caseLinkingEnabled,
    appliedCustomFieldSearch,
  );

  const { customFieldsEnabled, allCustomFields } = useSpeakUpCustomFields();

  const hasActiveSearchValue =
    Array.isArray(searchValue) && searchValue.length > 0;
  const hasActiveFilter = Object.keys(appliedFilterValue).length > 0;
  const hasActiveSearchOrFilter = Boolean(
    hasActiveSearchValue || searchQuery || activeCard || hasActiveFilter,
  );

  useEffect(() => {
    if (paramSearchValues && paramSearchValues.length > 0) {
      setSearchValue(paramSearchValues);
    }
  }, [paramSearchValues]);

  const currentUser = useSelector(
    (state: Types.State) => state.globalUsers.currentUser,
  );
  const hasListAllCases =
    currentUser?.systemPermissions?.includes('LIST_ALL_CASES');
  const caseDataDownloadEnabled =
    hasListAllCases && caseDataDownloadFeatureFlagEnabled;

  // Reset page number on error
  useEffect(() => {
    if (isError) {
      setPage(1);
    }
  }, [setPage, isError]);

  // Update number of pages when table data or rows change
  useEffect(() => {
    if (count) {
      setNumPages(Math.ceil(count / rows));
    }
  }, [count, rows]);

  // Set page to highest page number if greater than max
  useEffect(() => {
    if (Number(page) > numPages) {
      setPage(numPages);
    }
  }, [numPages, page, setPage]);

  const CARDS: { id: string; label: string; tooltip: string }[] = [
    {
      id: 'caseLead',
      label: 'Cases assigned to you as a case lead',
      tooltip: 'Cases where you are the case lead',
    },
    {
      id: 'teamMember',
      label: 'Cases assigned to you as a team member',
      tooltip: 'Cases where you are a team member',
    },
    {
      id: 'waitingForResponse',
      label: 'Waiting for response',
      tooltip: 'New cases pending review',
    },
  ];

  const onCardClick = (id: string) => {
    if (id === activeCard) {
      setActiveCard(null);
      setAppliedFilters({});
      setCaseQueryParamFields(null);
      return;
    }
    setActiveCard(id);
    setPage(1);
    if (id === 'caseLead') {
      setAppliedFilters({ caseLead: true, teamMember: false });
    } else if (id === 'teamMember') {
      setAppliedFilters({ caseLead: false, teamMember: true });
    } else {
      setAppliedFilters({ caseLead: false, teamMember: false });
      setCaseStatusTab('open');
    }
    setCaseQueryParamFields(null);
  };

  const handleOnSearchChange = (
    newValue: unknown,
    actionMeta: ActionMeta<unknown>,
  ) => {
    const selectedOption = newValue as SearchValueProps;
    searchCases?.(selectedOption);
    setSearchValue(selectedOption);
    updateUrlParams?.(selectedOption);

    if (
      (actionMeta.action === 'clear' || actionMeta.action === 'pop-value') &&
      setCaseQueryParamFields &&
      resetParams
    ) {
      setCaseQueryParamFields(null);
      resetParams();
    }
  };

  const onReset = () => {
    setSearchValue(null);
    setSearchQuery('');
    setActiveCard(null);
    setAppliedFilters({});
    setCaseStatusTab('open');
    setCaseQueryParamFields(null);
    resetParams?.();
    onRemoveAllFilters();
  };

  const metrics = useCaseMetrics();

  return (
    <AtlasMuiThemeProvider>
      <PageContentBox
        className="routing-body-content"
        data-testid="speak-up-table-page"
      >
        {showRedirect && (
          <Redirect data-testid={redirectTo} to={`${redirectTo}`} push />
        )}

        <SpeakUpCasesHeader
          modernizationEnabled={modernizationEnabled}
          caseDataDownloadEnabled={caseDataDownloadEnabled}
          addNewReport={addNewReport}
          addExistingReport={addExistingReport}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          {CARDS.map((card) => {
            return (
              <HeroMetricCard
                key={card.id}
                data-testid={`hero-metric-card-${card.id}`}
                label={card.label}
                infoTooltip={card.tooltip}
                count={metrics[card.id as keyof typeof useCaseMetrics] ?? 0}
                isLoading={metrics.isLoading}
                selected={activeCard === card.id}
                onClick={() => onCardClick(card.id)}
              />
            );
          })}
        </Box>

        <Box>
          {modernizationEnabled ? (
            <FilterToolbar
              search={
                <FormControl>
                  <FormLabel htmlFor="cases-global-search">Search</FormLabel>
                  <TextField
                    placeholder="Search by all fields"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      searchCases?.(
                        value ? [{ label: value, value, __isNew__: true }] : [],
                      );
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <StyledIcon
                              className="uil uil-search"
                              size="20px"
                            />
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        id: 'cases-global-search',
                        'data-testid': 'cases-global-search',
                      },
                    }}
                    data-testid="cases-search-input-wrapper"
                  />
                </FormControl>
              }
              buttons={
                <Button
                  variant="text"
                  data-testid="speak-up-toolbar-reset"
                  onClick={onReset}
                  startIcon={<UndoIcon />}
                >
                  Reset
                </Button>
              }
              actions={
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={caseStatusTab}
                  onChange={handleCaseStatusTabChange}
                  sx={toggleGroup}
                >
                  <ToggleButton
                    value="open"
                    data-testid="cases-toolbar-toggle-open"
                  >
                    Open
                  </ToggleButton>
                  <ToggleButton
                    value="closed"
                    data-testid="cases-toolbar-toggle-closed"
                  >
                    Closed
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            />
          ) : (
            <FilterToolbar
              search={
                <Box sx={{ minWidth: '250px', maxWidth: '700px' }}>
                  <CreatableSelect
                    placeholder="Search"
                    options={caseSearchOptions}
                    value={searchValue}
                    onChange={handleOnSearchChange}
                    isMulti
                    isClearable
                  />
                </Box>
              }
              buttons={
                <>
                  <Button
                    data-testid="speak-up-toolbar-filter"
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      setFilterAnchorEl(e.currentTarget)
                    }
                    style={styles.filterButton}
                    startIcon={<FilterIcon />}
                  >
                    Filter
                  </Button>

                  <Button
                    sx={{ minWidth: '80px' }}
                    variant="text"
                    data-testid="speak-up-toolbar-reset"
                    onClick={onReset}
                    startIcon={<UndoIcon />}
                  >
                    Reset
                  </Button>
                </>
              }
            />
          )}
        </Box>
        <SpeakUpFilterPopover
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          rows={filterRows}
          onRowsChange={onFilterRowsChange}
          value={filterValue}
          onChange={onFilterChange}
          onApply={(value, ref) => {
            onFilterApply(value, ref);
            setFilterAnchorEl(null);
          }}
          onRemoveAll={() => {
            onRemoveAllFilters();
            setFilterAnchorEl(null);
          }}
          makeId={makeFilterId}
          customFieldsEnabled={customFieldsEnabled}
          customFields={allCustomFields}
        />
        <SpeakUpTable
          data-testid="cases-table"
          caseLinkingEnabled={caseLinkingEnabled}
          sortableColumns={sortableColumns}
          sortedColumn={sortedColumn}
          sortColumns={sortColumns}
          columns={columns(caseLinkingEnabled)}
          rows={tableData as { tableRowData: RowModel }[]}
          onPageChange={(index: number) => setPage(index)}
          isLoading={isLoading}
          isError={isError}
          page={Number(page)}
          totalPages={numPages}
          onRowClick={openReport}
          emptyState={
            <EmptyState
              illustration={
                <img
                  src={emptyStateImage}
                  alt="No cases"
                  width="160"
                  height="132"
                />
              }
              title={
                hasActiveSearchOrFilter
                  ? 'No results found'
                  : modernizationEnabled
                    ? 'No cases found'
                    : 'No cases available'
              }
              description={
                hasActiveSearchOrFilter
                  ? 'Review your filters or refine your search to view more results.'
                  : modernizationEnabled
                    ? 'You can add a case by clicking "Add new case".'
                    : 'You have currently no cases in the system.'
              }
              cta={
                modernizationEnabled && !hasActiveSearchOrFilter ? (
                  <CaseCreateNewButton
                    addNewReport={addNewReport}
                    addExistingReport={addExistingReport}
                  />
                ) : undefined
              }
            />
          }
        />
      </PageContentBox>
    </AtlasMuiThemeProvider>
  );
};
